import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { WorkspaceDeliveryFormat } from '../lib/api/reporting';
import type {
  WorkspaceDetails,
  WorkspacePayload,
  WorkspaceStatus,
  WorkspaceStatusConfig,
} from '../lib/api/workspaces/dto';
import { STORE_NAMES } from './constants';

type Modify<T, R> = Omit<T, keyof R> & R;

export interface IWorkspaceFile {
  fileName: string;
  format: WorkspaceDeliveryFormat;
  generatedAt: string;
  rendererTaskID: string;
}

export interface IWorkspaceDeliveryDetails {
  deliveryCron: string;
  deliveryDashboardID: string;
  deliveryFormat: WorkspaceDeliveryFormat;
  deliveryName: string;
  deliveryRecipients: string;
  deliveryTimezone: string;
  files: IWorkspaceFile[];
}

export interface IWorkspaceWebhooksDetails {
  workspaceID: number;
  method: string;
  url: string;
  priming: boolean;
  enabled: boolean;
  contentType?: string;
  body?: string;
  createdAt: string;
  updatedAt: string;
}

export type IWorkspaceObject = Modify<
  WorkspacePayload,
  {
    workspace_stats: Record<string, string>;
    workspace_looker_environment_details: Record<string, string>;
    workspace_table_details: Record<string, string>;
    workspace_schedule_details: { cron: string };
    workspace_delivery_details: IWorkspaceDeliveryDetails;
    workspace_looker_dashboard_details: string;
    workspace_webhooks_details: IWorkspaceWebhooksDetails;
  }
>;

interface WorkspaceState {
  currentWorkspace: IWorkspaceObject | WorkspacePayload;
  lookerDashboardID?: string;
  isEditingWorkspace: boolean;
  setIsEditingWorkspace: (isEditingWorkspace: boolean) => void;
  setCurrentWorkspace: (workspace: IWorkspaceObject | WorkspacePayload) => void;
  setCurrentWorkspaceDetails: (details: WorkspaceDetails) => void;
  setCurrentWorkspaceName: (name: string) => void;
  setCurrentWorkspaceStatus: (status: WorkspaceStatus) => void;
  setCurrentWorkspaceConfig: (config: WorkspaceStatusConfig) => void;
  setCurrentWorkspaceAdvertiserID: (id: number) => void;
  setLookerDashboardID: (id: string) => void;
  clearCurrentWorkspace: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  devtools(
    (set) => ({
      currentWorkspace: undefined,
      lookerDashboardID: undefined,
      isEditingWorkspace: false,
      setIsEditingWorkspace: (isEditingWorkspace) =>
        set({ isEditingWorkspace }, undefined, 'workspace/setIsEditingWorkspace'),
      setCurrentWorkspace: (workspace) => set({ currentWorkspace: workspace }, undefined, 'setCurrentWorkspace'),
      setCurrentWorkspaceAdvertiserID: (id: number) =>
        set(
          (state) => ({
            currentWorkspace: state.currentWorkspace
              ? { ...state.currentWorkspace, workspace_advertiser_id: id }
              : undefined,
          }),
          undefined,
          'workspace/setCurrentWorkspaceAdvertiserID',
        ),
      setCurrentWorkspaceDetails: (details) =>
        set(
          (state) => ({
            currentWorkspace: state.currentWorkspace
              ? {
                  ...state.currentWorkspace,
                  workspace_details: {
                    ...state.currentWorkspace.workspace_details,
                    ...details,
                  },
                }
              : undefined,
          }),
          undefined,
          'setCurrentWorkspace',
        ),
      setCurrentWorkspaceName: (name) =>
        set(
          (state) => ({
            currentWorkspace: state.currentWorkspace ? { ...state.currentWorkspace, workspace_name: name } : undefined,
          }),
          undefined,
          'workspace/setCurrentWorkspaceName',
        ),
      setCurrentWorkspaceStatus: (status) =>
        set(
          (state) => ({
            currentWorkspace: state.currentWorkspace
              ? { ...state.currentWorkspace, workspace_status_id: status }
              : undefined,
          }),
          undefined,
          'workspace/setCurrentWorkspaceStatus',
        ),
      setCurrentWorkspaceConfig: (config) =>
        set(
          (state) => ({
            currentWorkspace: state.currentWorkspace
              ? { ...state.currentWorkspace, workspace_status_config: config }
              : undefined,
          }),
          undefined,
          'workspace/setCurrentWorkspaceConfig',
        ),
      setLookerDashboardID: (id) => set({ lookerDashboardID: id }, undefined, 'workspace/setLookerDashboardID'),
      clearCurrentWorkspace: () =>
        set(
          {
            currentWorkspace: undefined,
            lookerDashboardID: undefined,
            isEditingWorkspace: false,
          },
          undefined,
          'workspace/clearCurrentWorkspace',
        ),
    }),
    {
      name: STORE_NAMES.WORKSPACE,
      enabled: process.env.NODE_ENV === 'development',
    },
  ),
);
